import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Renderer2 } from '@angular/core';
import { RouterLink } from '@angular/router';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';
import { DfUiCarouselModule } from '@df/ui-carousel/carousel.module';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { BgColorOptions, ColorOptions } from '@df/ui/atomic/atomic-options';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ButtonStyleComponent } from '../../common/buttons/button-style/button-style.component';
import { NTextBox, TextBoxComponent } from '../../common/text-box/text-box.component';
import { defaultImage } from '../../icms.config';

let uid = 0;
export namespace NCmsCategoryRouting {
    export const ID = 'category-routing';

    export interface IData extends NICmsContentComponent.IData, NTextBox.IData {
        backgroundColor?: string;
        isCarousel: boolean;
        itemsAlternativeVariant: boolean;
        items: IItem[];
    }

    interface IItem {
        image: string;
        link: string;
        title?: string;
        titleColor?: string;
        description?: string;
        descriptionColor?: string;
        buttonLabel?: string;
        buttonColor?: string;
    }
}
@ICmsComponent<CmsCategoryRoutingComponent>({
    id: NCmsCategoryRouting.ID,
    name: 'Category routing',
    tags: ['Category', 'Grid', 'Carousel'],
    fields: [
        <NICmsContentField.IChoiceField>{
            key: 'backgroundColor',
            type: NICmsContentField.EFieldType.choice,
            previewMode: 'color',
            title: 'Backgroundcolor',
            options: BgColorOptions,
            default: undefined
        },
        <NICmsContentField.IToggleField>{
            key: 'isCarousel',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Mode',
            labels: { true: 'Carousel', false: 'Grid' },
            default: false,
            required: true
        },
        {
            key: 'headerLabel',
            type: NICmsContentField.EFieldType.label,
            title: 'Header'
        },
        ...NTextBox.getFields(false, false),
        <NICmsContentField.IToggleField>{
            key: 'itemsAlternativeVariant',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Items variant',
            labels: { true: 'Variant 2', false: 'Variant 1' },
            default: false,
            required: true
        },
        // Variant 1 items
        <NICmsContentField.IRepeaterField>{
            key: 'items',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Items',
            itemName: 'Item',
            itemNameField: 'title',
            required: true,
            canShow: (data: NCmsCategoryRouting.IData) => !data.itemsAlternativeVariant,
            fields: [
                {
                    key: 'image',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Image',
                    default: defaultImage,
                    required: true
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    description: 'Link will be applied to the whole element',
                    default: '/',
                    required: true
                },
                {
                    key: 'title',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Title',
                    default: 'Category name'
                },
                {
                    key: 'titleColor',
                    type: NICmsContentField.EFieldType.choice,
                    previewMode: 'color',
                    title: 'Title color',
                    options: ColorOptions,
                    default: 'col-1'
                },
                {
                    key: 'description',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Description',
                    default: 'Category description'
                },
                {
                    key: 'descriptionColor',
                    type: NICmsContentField.EFieldType.choice,
                    previewMode: 'color',
                    title: 'Description color',
                    options: ColorOptions,
                    default: 'col-11'
                },
                {
                    key: 'buttonLabel',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Button label',
                    default: 'Button label'
                },
                {
                    key: 'buttonColor',
                    type: NICmsContentField.EFieldType.choice,
                    previewMode: 'color',
                    title: 'Button color',
                    options: ColorOptions
                }
            ],
            defaultSize: 3
        },
        // Variant 2 items
        <NICmsContentField.IRepeaterField>{
            key: 'items',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Items',
            itemName: 'Item',
            itemNameField: 'buttonLabel',
            required: true,
            canShow: (data: NCmsCategoryRouting.IData) => data.itemsAlternativeVariant,
            fields: [
                {
                    key: 'image',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Image',
                    default: defaultImage,
                    required: true
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    description: 'Link will be applied to the whole element',
                    default: '/',
                    required: true
                },
                {
                    key: 'buttonLabel',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Button label',
                    default: 'Button label'
                }
            ],
            defaultSize: 3
        }
    ]
})
@Component({
    imports: [
        ButtonStyleComponent,
        DfUiCarouselModule,
        CommonModule,
        GridModule,
        ICmsClientModule,
        DfModuleImageModule,
        TextBoxComponent,
        RouterLink
    ],
    selector: 'cms-category-routing',
    templateUrl: './cms-category-routing.html',
    styleUrl: 'cms-category-routing.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CmsCategoryRoutingComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: NCmsCategoryRouting.IData;

    readonly uid = `cms-category-routing-${uid++}`;

    private readonly renderer2 = inject(Renderer2);

    mouseEnterHandler(event): void {
        this.renderer2.addClass(event.target?.lastElementChild, 'active');
    }

    mouseLeaveHandler(event): void {
        this.renderer2.removeClass(event.target?.lastElementChild, 'active');
    }
}
